// src/app/core/config/config.service.ts
import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { RuntimeConfig } from './runtime-config.model';

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private readonly http = inject(HttpClient);

  /** Señal privada — acceder solo a través de get() */
  private readonly _config = signal<RuntimeConfig | null>(null);

  /**
   * Llamado por APP_INITIALIZER en app.config.ts.
   * Bloquea el bootstrap de Angular hasta que la configuración esté disponible.
   * Si falla: muestra error en #app-loading y detiene el bootstrap.
   */
  async load(): Promise<void> {
    try {
      const cfg = await firstValueFrom(
        this.http.get<RuntimeConfig>(environment.runtimeConfigUrl)
      );
      this.validateConfig(cfg);
      this._config.set(cfg);
    } catch (err) {
      // Si el config no carga, la app no puede funcionar.
      // Reescribir el loading screen con un mensaje de error descriptivo.
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      const el = document.getElementById('app-loading');
      if (el) {
        el.innerHTML = `
          <div class="config-error" role="alert">
            <h2>No se pudo iniciar la aplicación</h2>
            <p>Hubo un problema al cargar la configuración del sistema.</p>
            <p>Por favor contacte al administrador.</p>
            <p style="color:var(--color-text-light);font-size:var(--font-size-sm);margin-top:var(--space-4);">
              Detalle: ${this.sanitizeForHtml(errorMessage)}
            </p>
          </div>`;
      }
      // Relanzar para que APP_INITIALIZER falle y el bootstrap se detenga
      throw err;
    }
  }
  
  /** true una vez que load() completó exitosamente */
  isLoaded(): boolean {
    return this._config() !== null;
  }

  /**
   * Accede a una sección de la configuración de forma type-safe.
   * Lanza error si se llama antes de que load() complete.
   */
  get<K extends keyof RuntimeConfig>(key: K): RuntimeConfig[K] {
    const cfg = this._config();
    if (!cfg) {
      throw new Error(
        `ConfigService.get('${String(key)}'): la configuración no ha sido cargada. ` +
        `Verificar que APP_INITIALIZER esté configurado correctamente.`
      );
    }
    return cfg[key];
  }

  /**
   * Valida que el schema del runtime-config.json sea correcto.
   * Lanza un Error descriptivo indicando exactamente qué campo está mal.
   */
  private validateConfig(cfg: unknown): void {
    if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) {
      throw new Error('runtime-config.json: el contenido debe ser un objeto JSON válido');
    }
    const c = cfg as Record<string, unknown>;

    // ── Secciones requeridas ────────────────────────────────────────────────
    const sections: [string][] = [
      ['api'], ['tenant'], ['session'], ['institution'], ['i18n'], ['features'], ['logging'], ['export'],
    ];
    for (const [field] of sections) {
      if (typeof c[field] !== 'object' || Array.isArray(c[field]) || c[field] === null) {
        throw new Error(
          `runtime-config.json: la sección '${field}' es requerida y debe ser un objeto`
        );
      }
    }

    // ── api ─────────────────────────────────────────────────────────────────
    const api = c['api'] as Record<string, unknown>;
    if (typeof api['baseUrl'] !== 'string' || !api['baseUrl']) {
      throw new Error("runtime-config.json: 'api.baseUrl' debe ser un string no vacío");
    }
    if (typeof api['changePasswordUrl'] !== 'string' || !api['changePasswordUrl']) {
      throw new Error("runtime-config.json: 'api.changePasswordUrl' debe ser un string no vacío");
    }
    if (typeof api['timeoutMs'] !== 'number' || (api['timeoutMs'] as number) <= 0) {
      throw new Error("runtime-config.json: 'api.timeoutMs' debe ser un número positivo");
    }
    if (typeof api['retryAttemptsOnGet'] !== 'number' || (api['retryAttemptsOnGet'] as number) < 0) {
      throw new Error("runtime-config.json: 'api.retryAttemptsOnGet' debe ser un número >= 0");
    }

    // ── tenant ──────────────────────────────────────────────────────────────
    const tenant = c['tenant'] as Record<string, unknown>;
    if (typeof tenant['id'] !== 'string' || !tenant['id']) {
      throw new Error("runtime-config.json: 'tenant.id' debe ser un string no vacío");
    }
    if (!['single', 'subdomain', 'param'].includes(tenant['mode'] as string)) {
      throw new Error("runtime-config.json: 'tenant.mode' debe ser 'single' | 'subdomain' | 'param'");
    }

    // ── session ─────────────────────────────────────────────────────────────
    // Invariantes reforzadas en el Prompt 19, Fase 3: a 30/28 minutos un
    // JSON mal editado casi nunca se nota; a 5/4 minutos (o peor) el margen
    // de aviso puede reducirse a un sobresalto sin que nada lo impida. El
    // control falla deteniendo el arranque con un mensaje claro, no con un
    // comportamiento indefinido en producción.
    const s = c['session'] as Record<string, unknown>;
    if (typeof s['timeoutMinutes'] !== 'number' || (s['timeoutMinutes'] as number) <= 0) {
      throw new Error("runtime-config.json: 'session.timeoutMinutes' debe ser un número positivo");
    }
    if (typeof s['warningMinutes'] !== 'number' || (s['warningMinutes'] as number) <= 0) {
      throw new Error("runtime-config.json: 'session.warningMinutes' debe ser un número positivo");
    }
    if ((s['warningMinutes'] as number) >= (s['timeoutMinutes'] as number)) {
      throw new Error(
        "runtime-config.json: 'session.warningMinutes' debe ser menor que 'session.timeoutMinutes'"
      );
    }
    const warningMarginSeconds = ((s['timeoutMinutes'] as number) - (s['warningMinutes'] as number)) * 60;
    if (warningMarginSeconds < 30) {
      throw new Error(
        "runtime-config.json: el margen entre 'session.warningMinutes' y 'session.timeoutMinutes' " +
        "debe ser de al menos 30 segundos — menos que eso no es un aviso, es un sobresalto"
      );
    }
    if (typeof s['maxLoginAttempts'] !== 'number' || (s['maxLoginAttempts'] as number) <= 0) {
      throw new Error("runtime-config.json: 'session.maxLoginAttempts' debe ser un número positivo");
    }
    if (typeof s['lockoutSeconds'] !== 'number' || (s['lockoutSeconds'] as number) < 0) {
      throw new Error("runtime-config.json: 'session.lockoutSeconds' debe ser un número >= 0");
    }

    // ── institution ─────────────────────────────────────────────────────────
    const inst = c['institution'] as Record<string, unknown>;
    if (typeof inst['name'] !== 'string' || !inst['name']) {
      throw new Error("runtime-config.json: 'institution.name' debe ser un string no vacío");
    }
    if (typeof inst['shortName'] !== 'string' || !inst['shortName']) {
      throw new Error("runtime-config.json: 'institution.shortName' debe ser un string no vacío");
    }
    if (typeof inst['primaryColor'] !== 'string' || !inst['primaryColor']) {
      throw new Error("runtime-config.json: 'institution.primaryColor' debe ser un string no vacío");
    }
    if (inst['primaryColorDark'] !== undefined
      && (typeof inst['primaryColorDark'] !== 'string' || !inst['primaryColorDark'])) {
      throw new Error("runtime-config.json: 'institution.primaryColorDark', si está presente, debe ser un string no vacío");
    }
    if (typeof inst['logoUrl'] !== 'string' || !inst['logoUrl']) {
      throw new Error("runtime-config.json: 'institution.logoUrl' debe ser un string no vacío");
    }
    if (typeof inst['supportEmail'] !== 'string' || !inst['supportEmail']) {
      throw new Error("runtime-config.json: 'institution.supportEmail' debe ser un string no vacío");
    }
    if (typeof inst['timezone'] !== 'string' || !inst['timezone']) {
      throw new Error("runtime-config.json: 'institution.timezone' debe ser un string no vacío (ej: 'America/Guatemala')");
    }

    // ── i18n ────────────────────────────────────────────────────────────────
    const i18n = c['i18n'] as Record<string, unknown>;
    if (typeof i18n['defaultLanguage'] !== 'string' || !i18n['defaultLanguage']) {
      throw new Error("runtime-config.json: 'i18n.defaultLanguage' debe ser un string no vacío");
    }
    if (!Array.isArray(i18n['supportedLanguages']) || (i18n['supportedLanguages'] as unknown[]).length === 0) {
      throw new Error("runtime-config.json: 'i18n.supportedLanguages' debe ser un array no vacío");
    }
    if (typeof i18n['storageKey'] !== 'string' || !i18n['storageKey']) {
      throw new Error("runtime-config.json: 'i18n.storageKey' debe ser un string no vacío");
    }
    if (typeof i18n['assetsPath'] !== 'string' || !i18n['assetsPath']) {
      throw new Error("runtime-config.json: 'i18n.assetsPath' debe ser un string no vacío");
    }

    // ── features ────────────────────────────────────────────────────────────
    const features = c['features'] as Record<string, unknown>;
    const featureKeys = [
      'savingsModule', 'loanRepaymentSchedule', 'changePassword',
      'exportCsv', 'exportPdf', 'exportExcel', 'multipleClientsPerUser',
      'languageSwitcherInTopbar', 'darkMode',
    ] as const;
    for (const key of featureKeys) {
      if (typeof features[key] !== 'boolean') {
        throw new Error(`runtime-config.json: 'features.${key}' debe ser un boolean`);
      }
    }

    // ── logging ─────────────────────────────────────────────────────────────
    const log = c['logging'] as Record<string, unknown>;
    if (typeof log['enabled'] !== 'boolean') {
      throw new Error("runtime-config.json: 'logging.enabled' debe ser un boolean");
    }
    if (typeof log['endpoint'] !== 'string') {
      throw new Error("runtime-config.json: 'logging.endpoint' debe ser un string");
    }
    if (!['error', 'warn', 'info'].includes(log['level'] as string)) {
      throw new Error("runtime-config.json: 'logging.level' debe ser 'error' | 'warn' | 'info'");
    }

    // ── export (Prompt 18) ─────────────────────────────────────────────────
    const exp = c['export'] as Record<string, unknown>;
    if (typeof exp['maxRows'] !== 'number' || (exp['maxRows'] as number) <= 0) {
      throw new Error("runtime-config.json: 'export.maxRows' debe ser un número positivo");
    }
    if (typeof exp['includeInstitutionHeader'] !== 'boolean') {
      throw new Error("runtime-config.json: 'export.includeInstitutionHeader' debe ser un boolean");
    }
    if (!['A4', 'Letter'].includes(exp['pdfPageSize'] as string)) {
      throw new Error("runtime-config.json: 'export.pdfPageSize' debe ser 'A4' | 'Letter'");
    }
    if (!['portrait', 'landscape'].includes(exp['pdfOrientationDefault'] as string)) {
      throw new Error("runtime-config.json: 'export.pdfOrientationDefault' debe ser 'portrait' | 'landscape'");
    }
    if (typeof exp['chunkTimeoutMs'] !== 'number' || (exp['chunkTimeoutMs'] as number) <= 0) {
      throw new Error("runtime-config.json: 'export.chunkTimeoutMs' debe ser un número positivo");
    }
  }

  /** Escapa HTML básico para mostrar mensajes de error sin riesgo de XSS */
  private sanitizeForHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
